import { Link } from "react-router-dom";
import {
  ArrowRight,
  Handshake,
  GraduationCap,
  Building2,
  Mic,
  Scale,
  Briefcase,
  Mail,
  CheckCircle2,
  Network,
  Globe2,
  Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// ── Reusable list with neat checkmark bullets ────────────────────────────
const BulletList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-primary" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// ── Reusable opportunity block (icon + heading + lead + bullets + close) ─
const OpportunityBlock = ({
  testId,
  overline,
  title,
  Icon,
  iconBg,
  iconColor,
  lead,
  bullets,
  closing,
  bg = "bg-white",
}) => (
  <section className={`py-16 lg:py-20 ${bg}`} data-testid={testId}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-[auto,1fr] gap-8 lg:gap-12 items-start max-w-5xl mx-auto">
        <div
          className={`w-16 h-16 lg:w-20 lg:h-20 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}
        >
          <Icon className={`w-8 h-8 lg:w-10 lg:h-10 ${iconColor}`} />
        </div>
        <div>
          <p className="overline text-brand-primary mb-3">{overline}</p>
          <h2 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-5 leading-snug">
            {title}
          </h2>
          {lead.map((p, i) => (
            <p
              key={i}
              className="text-slate-700 leading-relaxed mb-4 last:mb-0"
            >
              {p}
            </p>
          ))}
          {bullets && bullets.length > 0 && (
            <div className="mt-6 bg-slate-50 rounded-xl p-5 lg:p-6 border border-slate-200">
              <BulletList items={bullets} />
            </div>
          )}
          {closing && (
            <p className="text-slate-700 leading-relaxed mt-6">{closing}</p>
          )}
        </div>
      </div>
    </div>
  </section>
);

export const OpportunitiesPage = () => {
  return (
    <div id="main-content" className="pt-20" data-testid="opportunities-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950" />
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 25% 40%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.12), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-blue-300 mb-4">Opportunities</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
              Collaborate with the Centre for Consciousness Studies
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-5">
              We welcome opportunities to collaborate with researchers, universities, industry partners, conference organisers, policy leaders, and institutions working across consciousness studies and related fields.
            </p>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Our work sits at the intersection of philosophy of mind, artificial intelligence, neurotechnology, brain–computer interface research, quantum computing, and fundamental physics. We are especially interested in collaborations that bring these domains into meaningful dialogue and support deeper, more integrated approaches to understanding mind, intelligence, reality, and human-centred technological development.
            </p>
            <p className="text-base sm:text-lg text-slate-200 leading-relaxed mt-5 font-medium">
              If your work aligns with these areas, we encourage you to connect with us.
            </p>
          </div>
        </div>
      </section>

      {/* How We Work — network & think tank positioning */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="opp-how-we-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">How We Work</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              A Multidisciplinary Network for Responsible Innovation
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {/* Block 1 — partnership network */}
            <div className="grid sm:grid-cols-[auto,1fr] gap-5 sm:gap-6 items-start bg-white rounded-xl p-6 lg:p-8 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Network className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-slate-900 mb-2">
                  Partnership and collaborative network
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We operate through a strong partnership and collaborative network, serving as a multidisciplinary think tank that brings together expertise across consciousness studies, philosophy of mind, artificial intelligence, neurotechnology, fundamental physics, and broader STEM disciplines.
                </p>
              </div>
            </div>

            {/* Block 2 — standards & policy track record */}
            <div className="grid sm:grid-cols-[auto,1fr] gap-5 sm:gap-6 items-start bg-slate-50 rounded-xl p-6 lg:p-8 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Globe2 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-slate-900 mb-2">
                  Engagement with peak bodies and standards organisations
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Our team has extensive experience engaging with global peak bodies, standards development organisations, policy development groups, and framework-setting institutions. This includes contributing to standards, policies, governance models, and practical frameworks that support responsible innovation, ethical technology development, and evidence-informed decision-making.
                </p>
              </div>
            </div>

            {/* Block 3 — core commitment */}
            <div className="grid sm:grid-cols-[auto,1fr] gap-5 sm:gap-6 items-start bg-slate-50 rounded-xl p-6 lg:p-8 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-slate-900 mb-2">
                  Consciousness studies as a rigorous interdisciplinary field
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  At the core of our work is a commitment to advancing consciousness studies as a rigorous interdisciplinary field. We explore philosophy of mind in connection with science, technology, engineering, and mathematics, creating new pathways for research collaboration, policy engagement, and practical impact across academia, industry, government, and international networks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1. Research Collaboration & Grants */}
      <OpportunityBlock
        testId="opp-research-grants"
        overline="Research & Grants"
        title="Research Collaboration and Grant Opportunities"
        Icon={Handshake}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
        bg="bg-white"
        lead={[
          "We are open to research partnerships that support ambitious, interdisciplinary work with strong intellectual and public value.",
          "This includes collaboration on relevant grant opportunities, particularly those that engage with one or more of the following areas:",
        ]}
        bullets={[
          "Philosophy of mind",
          "Consciousness studies",
          "Neurotechnology",
          "Artificial intelligence",
          "Brain–computer interfaces",
          "Quantum computing",
          "Fundamental physics",
          "Human-centred and responsible innovation",
        ]}
        closing="We are particularly interested in projects that include a meaningful philosophy of mind component while also engaging scientific, technical, or societal questions. We welcome conversations with academic collaborators, research institutes, industry partners, and funding-focused teams seeking to build serious, forward-looking proposals."
      />

      {/* 2. University Engagement */}
      <OpportunityBlock
        testId="opp-university"
        overline="Universities & Supervision"
        title="University Engagement and Research Supervision"
        Icon={GraduationCap}
        iconBg="bg-purple-100"
        iconColor="text-purple-600"
        bg="bg-slate-50"
        lead={[
          "We are keen to engage with universities and higher education institutions on research supervision and academic collaboration.",
          "This may include:",
        ]}
        bullets={[
          "PhD research opportunities",
          "Capstone and honours research projects",
          "Interdisciplinary academic partnerships",
          "Co-supervision and research mentoring",
          "Grant-supported university collaboration",
          "Industry-linked research engagement",
        ]}
        closing="We bring significant experience working across research, engineering, AI governance, and interdisciplinary inquiry. We are particularly interested in supporting projects that connect rigorous scholarship with emerging challenges in technology, ethics, cognition, and the future of human-centred systems."
      />

      {/* 3. Industry, Innovation, Strategic Engagement */}
      <OpportunityBlock
        testId="opp-industry"
        overline="Industry & Innovation"
        title="Industry, Innovation, and Strategic Engagement"
        Icon={Building2}
        iconBg="bg-emerald-100"
        iconColor="text-emerald-600"
        bg="bg-white"
        lead={[
          "We welcome engagement with organisations exploring the practical implications of consciousness-related research in technology, design, governance, and innovation.",
          "This includes opportunities connected to:",
        ]}
        bullets={[
          "AI and human–AI collaboration",
          "Neurotechnology and BCI",
          "Responsible innovation",
          "Decision-making and accountability",
          "Emerging technology strategy",
          "Cross-sector research translation",
        ]}
        closing="We are interested in partnerships that combine conceptual depth with real-world relevance, especially where new technologies raise important questions about human agency, interpretation, ethics, and system design."
      />

      {/* 4. Conferences, Speaking, Public Forums */}
      <OpportunityBlock
        testId="opp-speaking"
        overline="Conferences & Public Forums"
        title="Conferences, Speaking, and Public Forums"
        Icon={Mic}
        iconBg="bg-rose-100"
        iconColor="text-rose-600"
        bg="bg-slate-50"
        lead={[
          "We are available to contribute to conferences, research forums, expert panels, workshops, and public conversations.",
          "Our work can support broader discussion in areas such as:",
        ]}
        bullets={[
          "Consciousness studies",
          "Philosophy of mind",
          "Neurotechnology",
          "Artificial intelligence",
          "Brain–computer interfaces",
          "Quantum computing",
          "Fundamental physics",
          "Responsible AI",
          "Ethics, governance, standards, and public policy",
        ]}
        closing="We welcome invitations from conference organisers, academic networks, industry forums, and public-interest platforms seeking thoughtful, interdisciplinary perspectives on these rapidly evolving fields."
      />

      {/* 5. Standards, Regulation, Policy */}
      <OpportunityBlock
        testId="opp-standards-policy"
        overline="Standards & Policy"
        title="Standards, Regulation, and Policy Engagement"
        Icon={Scale}
        iconBg="bg-amber-100"
        iconColor="text-amber-600"
        bg="bg-white"
        lead={[
          "We are also open to engaging with peak bodies, standards communities, regulators, and policymakers.",
          "As emerging technologies reshape research, industry, and society, there is a growing need for deeper thinking about human-centred design, accountability, governance, and the role of consciousness and agency in technical systems. We welcome opportunities to contribute to discussions that help shape standards, regulatory thinking, and policy development in these areas.",
        ]}
      />

      {/* Current Openings */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="opp-current-openings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-slate-200 bg-white">
              <CardContent className="p-8 lg:p-10">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-7 h-7 text-slate-600" />
                  </div>
                  <div>
                    <p className="overline text-brand-primary mb-2">Current Openings</p>
                    <h3 className="font-serif text-2xl text-slate-900 mb-4 leading-snug">
                      No active career opportunities at this time
                    </h3>
                    <p className="text-slate-700 leading-relaxed">
                      We do not currently have active career opportunities listed. When future roles, research opportunities, or formal calls become available, they will be published on this page.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 lg:py-24 bg-white" data-testid="opp-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 rounded-2xl shadow-lg overflow-hidden relative">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(ellipse 70% 60% at 25% 40%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.18), transparent 60%)",
              }}
            />
            <div className="relative p-8 lg:p-14 text-center max-w-4xl mx-auto">
              <div className="inline-flex w-16 h-16 rounded-full bg-white/10 ring-1 ring-white/20 items-center justify-center mb-6 backdrop-blur-sm">
                <Mail className="w-8 h-8 text-blue-200" />
              </div>
              <p className="overline text-blue-300 mb-4">Get in Touch</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-white mb-6 leading-tight">
                Let's Start a Conversation
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                If you are interested in collaborating with us, applying for a relevant grant together, discussing supervision opportunities, inviting us to contribute to a conference or forum, or exploring policy, standards, or industry engagement, please use our contact form.
              </p>
              <p className="text-slate-300 leading-relaxed mb-8">
                We welcome thoughtful approaches from relevant stakeholders and interested parties, and we aim to respond promptly to serious enquiries.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-slate-900 hover:bg-slate-100"
                  data-testid="opp-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-slate-500 text-white hover:bg-white/10 bg-transparent"
                  data-testid="opp-impact-btn"
                >
                  <Link to="/impact">Explore Our Impact</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpportunitiesPage;
